import { InfoOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  Grid2 as Grid,
  Paper,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useCustomerApplications } from '@/api/financial/customer/list-customer-applications'
import { useReadUserProfile } from '@/api/user-manager'
import { AppHeader } from '@/components/app-header'
import { formatCurrency } from '@/helpers'
import { CustomerApplicationItem } from '@/types/customer-context'
import { ApplicationCard } from '../application-card'
import { CreditIdentity } from '../profile/credit-identity'
import { CrmDialog } from '../profile/crm-dialog'
import { ProfileDialog } from '../profile/profile-dialog'
import { profileOf } from '../products/credit-profile'
import { ProductsForYou } from '../products/products-for-you'
import { creditProductLabel } from '../credit-labels'

const closedChip: Record<
  'denied' | 'canceled',
  { label: string; color: 'error' | 'default' }
> = {
  denied: { label: 'Negado', color: 'error' },
  canceled: { label: 'Cancelado', color: 'default' },
}

export function CreditApplicationsPage() {
  const applications = useCustomerApplications()
  const { data: user } = useReadUserProfile()
  const [profileOpen, setProfileOpen] = useState(false)
  const [crmOpen, setCrmOpen] = useState(false)

  const profile = profileOf(user?.profile?.occupation)
  const inProgress = applications.data?.in_progress ?? []
  const closed = applications.data?.closed ?? []

  return (
    <>
      <AppHeader />

      <CreditIdentity />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {profile === 'student' && (
          <Grid size={12}>
            <ProfileHint
              text="Já se formou? Atualize seu perfil para Médico com o seu CRM e veja o Crédito para médicos."
              action="Atualizar perfil"
              onClick={() => setCrmOpen(true)}
            />
          </Grid>
        )}

        <Grid size={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" mb={2}>
              Em andamento
            </Typography>
            {inProgress.length > 0 ? (
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                {inProgress.map((application) => (
                  <ApplicationCard
                    key={application.application_key}
                    application={application}
                  />
                ))}
              </Box>
            ) : (
              <Typography color="text.disabled" textAlign="center" py={1}>
                {applications.isError
                  ? 'Não foi possível carregar as solicitações.'
                  : 'Nenhuma solicitação em andamento. Escolha um produto abaixo para começar.'}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid size={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" mb={1}>
              Encerradas
              {closed.length > 0 && (
                <Chip
                  label={closed.length}
                  size="small"
                  sx={{ ml: 1, bgcolor: 'primary.100', color: 'primary.main' }}
                />
              )}
            </Typography>
            {closed.length > 0 ? (
              closed.map((application) => (
                <ClosedRow
                  key={application.application_key}
                  application={application}
                />
              ))
            ) : (
              <Typography color="text.disabled" textAlign="center" py={1}>
                As solicitações negadas ou canceladas aparecem aqui.
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid size={12}>
          <ProductsForYou
            hasApplicationInProgress={inProgress.length > 0}
            currentProduct={inProgress[0]?.product}
          />
        </Grid>
      </Grid>

      <ProfileDialog
        key={user?.profile?.graduation_date ?? 'empty'}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        user={user}
        profile={profile}
        onUpdateToDoctor={() => {
          setProfileOpen(false)
          setCrmOpen(true)
        }}
      />
      {profile === 'student' && (
        <CrmDialog
          open={crmOpen}
          mode="switch"
          onClose={() => setCrmOpen(false)}
          onBack={() => {
            setCrmOpen(false)
            setProfileOpen(true)
          }}
        />
      )}
    </>
  )
}

function ProfileHint({
  text,
  action,
  onClick,
}: {
  text: string
  action: string
  onClick: () => void
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <InfoOutlined color="primary" fontSize="small" />
      <Typography variant="body2" color="text.secondary" flex={1}>
        {text}
      </Typography>
      <Button variant="text" size="small" onClick={onClick}>
        {action}
      </Button>
    </Box>
  )
}

function ClosedRow({ application }: { application: CustomerApplicationItem }) {
  const chip =
    application.status === 'denied' ? closedChip.denied : closedChip.canceled
  const closedAt = application.closed_at
    ? new Date(application.closed_at).toLocaleDateString('pt-BR')
    : null
  const amount = application.amount
    ? formatCurrency(Number(application.amount))
    : null

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: 1.5,
        alignItems: 'center',
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Box>
        <Typography>{creditProductLabel(application.product)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {[amount, closedAt ? `encerrada em ${closedAt}` : null]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      </Box>
      <Chip label={chip.label} color={chip.color} size="small" />
    </Box>
  )
}
